/**
 * Browser ML provider — ONNX Runtime Web implementation.
 * Uses WebGPU/WebGL/WASM backend detection, model loading
 * (MiniLM for embeddings, DistilBERT for classification),
 * and memory budget management.
 *
 * Offloads inference to a Web Worker (ml-worker.ts) to avoid
 * blocking the UI thread.
 */

import type {
  AIProviderAdapter,
  AICompletionRequest,
  AICompletionResponse,
} from './base-provider';
import { detectMLBackend, ML_INFERENCE_CONFIG } from '@/config/ml-config';
import type { MLBackend } from '@/config/ml-config';
import type { MLWorkerRequest, MLWorkerResponse } from '@/workers/ml-worker';

/** Singleton worker instance. */
let worker: Worker | null = null;
let workerReady = false;
let detectedBackend: MLBackend | null = null;
let requestCounter = 0;

/** Pending request callbacks. */
const pendingRequests = new Map<string, {
  resolve: (resp: MLWorkerResponse) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}>();

/** Initialize the ML worker. */
function ensureWorker(): Worker {
  if (worker) return worker;

  worker = new Worker(
    new URL('../../../workers/ml-worker.ts', import.meta.url),
    { type: 'module' },
  );

  worker.onmessage = (ev: MessageEvent<MLWorkerResponse>) => {
    const { id } = ev.data;
    const pending = pendingRequests.get(id);
    if (pending) {
      clearTimeout(pending.timer);
      pendingRequests.delete(id);
      pending.resolve(ev.data);
    }
  };

  worker.onerror = (err) => {
    console.error('[Sentinel] ML Worker error:', err);
    // Reject all pending requests
    for (const [id, pending] of pendingRequests) {
      clearTimeout(pending.timer);
      pending.reject(new Error('ML Worker crashed'));
      pendingRequests.delete(id);
    }
  };

  return worker;
}

/** Send a message to the worker and await the response. */
function sendToWorker(request: MLWorkerRequest): Promise<MLWorkerResponse> {
  const w = ensureWorker();
  const timeoutMs = ML_INFERENCE_CONFIG.workerTimeoutMs;

  return new Promise<MLWorkerResponse>((resolve, reject) => {
    const timer = setTimeout(() => {
      pendingRequests.delete(request.id);
      reject(new Error(`ML Worker timeout after ${timeoutMs}ms`));
    }, timeoutMs);

    pendingRequests.set(request.id, { resolve, reject, timer });
    w.postMessage(request);
  });
}

/** Generate a unique request ID. */
function nextRequestId(): string {
  return `ml-${++requestCounter}-${Date.now()}`;
}

/**
 * Compute embeddings for an array of texts.
 * Returns an array of float arrays (one per input text).
 */
export async function computeBrowserEmbeddings(texts: string[]): Promise<number[][]> {
  const batchSize = ML_INFERENCE_CONFIG.embeddingBatchSize;
  const allEmbeddings: number[][] = [];

  for (let i = 0; i < texts.length; i += batchSize) {
    const batch = texts.slice(i, i + batchSize);
    const response = await sendToWorker({
      id: nextRequestId(),
      type: 'embed',
      texts: batch,
    });

    if (response.type === 'error') {
      throw new Error(`Embedding failed: ${response.error}`);
    }

    if (response.embeddings) {
      allEmbeddings.push(...response.embeddings);
    }
  }

  return allEmbeddings;
}

/**
 * Classify an array of texts.
 * Returns label + confidence for each.
 */
export async function computeBrowserClassifications(texts: string[]): Promise<{ label: string; score: number }[]> {
  const batchSize = ML_INFERENCE_CONFIG.classificationBatchSize;
  const allResults: { label: string; score: number }[] = [];

  for (let i = 0; i < texts.length; i += batchSize) {
    const batch = texts.slice(i, i + batchSize);
    const response = await sendToWorker({
      id: nextRequestId(),
      type: 'classify',
      texts: batch,
    });

    if (response.type === 'error') {
      throw new Error(`Classification failed: ${response.error}`);
    }

    if (response.classifications) {
      allResults.push(...response.classifications);
    }
  }

  return allResults;
}

/**
 * Check if the ML worker is responsive.
 */
async function pingWorker(): Promise<boolean> {
  try {
    const response = await sendToWorker({ id: nextRequestId(), type: 'ping' });
    return response.type === 'pong';
  } catch {
    return false;
  }
}

/**
 * Terminate the ML worker and release resources.
 */
export function destroyBrowserML(): void {
  if (worker) {
    // Try graceful unload first
    sendToWorker({ id: nextRequestId(), type: 'unload' }).catch(() => {});
    setTimeout(() => {
      worker?.terminate();
      worker = null;
      workerReady = false;
    }, 1000);
  }

  for (const [, pending] of pendingRequests) {
    clearTimeout(pending.timer);
    pending.reject(new Error('ML Worker destroyed'));
  }
  pendingRequests.clear();
}

export class BrowserProvider implements AIProviderAdapter {
  readonly id = 'browser';
  readonly name = 'Browser ML (ONNX Runtime)';
  readonly supportsStructuredOutput = false;
  // complete() returns a classification string, not generated text
  readonly supportsGeneration = false;

  async isAvailable(): Promise<boolean> {
    // Detect backend capability
    if (!detectedBackend) {
      detectedBackend = await detectMLBackend();
    }

    if (detectedBackend === 'none') {
      return false;
    }

    // Try pinging the worker to confirm it can load
    if (!workerReady) {
      try {
        const alive = await pingWorker();
        workerReady = alive;
        return alive;
      } catch {
        return false;
      }
    }

    return true;
  }

  async listModels(): Promise<string[]> {
    return ['minilm-embeddings', 'distilbert-classification'];
  }

  async complete(request: AICompletionRequest): Promise<AICompletionResponse> {
    const start = performance.now();

    // Use classification model for completion-like requests
    // The browser provider can't do real text generation,
    // but it can provide classification-based responses
    const text = request.prompt;
    const classifications = await computeBrowserClassifications([text]);

    if (classifications.length === 0) {
      throw new Error('Browser ML: no classification result');
    }

    const result = classifications[0];
    const responseText = `[Browser ML] Sentiment: ${result.label} (confidence: ${(result.score * 100).toFixed(1)}%)`;

    return {
      text: responseText,
      model: 'distilbert-classification',
      provider: this.id,
      latencyMs: Math.round(performance.now() - start),
    };
  }

  async validate(): Promise<{ valid: boolean; error?: string }> {
    if (!detectedBackend) {
      detectedBackend = await detectMLBackend();
    }

    if (detectedBackend === 'none') {
      return { valid: false, error: 'No ML backend available (WebGPU, WebGL, or WASM required)' };
    }

    try {
      const alive = await pingWorker();
      if (!alive) {
        return { valid: false, error: 'ML Worker not responding' };
      }
      return { valid: true };
    } catch (err) {
      return { valid: false, error: err instanceof Error ? err.message : 'Validation failed' };
    }
  }
}
